import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import type { Account, Category, TransactionType } from '@zeni/shared'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useAmountFormat } from '../contexts/AmountUnitContext'
import { signPrefix } from '../lib/amount-format'
import type { TranslationKey } from '../i18n/translations'
import { reportsApi, accountsApi, categoriesApi } from '../lib/api'
import { CategoryCombobox } from '../components/CategoryCombobox'
import { btnAccentSm, btnNav, btnSecondarySm, pageSubtitle, pageTitle, segGroup, segItem } from '../lib/ui'

interface LedgerEntry {
  id: string
  date: string
  type: 'income' | 'expense'
  amount: number
  description: string
  accountId: string
  categoryId: string | null
  categoryName: string | null
  accountName: string
  /** 集計対象外。残高には効くが、月の収入・支出の合計からは外す */
  excluded?: boolean
}

interface DayRow {
  day: number
  weekday: number
  entries: LedgerEntry[]
  total: number
  balance: number
}

interface MonthBlock {
  month: number
  days: DayRow[]
  income: number
  expense: number
  closing: number
}

const DAY_KEYS: TranslationKey[] = [
  'common_day_sun',
  'common_day_mon',
  'common_day_tue',
  'common_day_wed',
  'common_day_thu',
  'common_day_fri',
  'common_day_sat',
]

/** 1か月ぶんの列幅（px）: 日付 / 説明 / 金額 / 合計 / 残高（説明は表示領域を1.3倍に拡大） */
const COL_WIDTHS = [46, 172, 76, 84, 94]
const MONTH_WIDTH = COL_WIDTHS.reduce((sum, w) => sum + w, 0)
/** 月ヘッダー行の高さ（px）— 列名行の sticky 位置に使う */
const MONTH_HEADER_HEIGHT = 34

type ViewMode = 'monthly' | 'annual'

/** 残高列の色分けしきい値（円） */
interface BalanceThresholds {
  low: number
  high: number
}

const DEFAULT_THRESHOLDS: BalanceThresholds = { low: 1_000_000, high: 3_000_000 }
const THRESHOLDS_STORAGE_KEY = 'zeni_ledger_thresholds'

const TONE_CLASSES = {
  low: 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  mid: 'bg-amber-50 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200',
  high: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
} as const

/** 残高としきい値から色トーンを決める（low 未満は赤、high 以上は緑、その間は黄） */
function balanceTone(balance: number, thresholds: BalanceThresholds): keyof typeof TONE_CLASSES {
  if (balance < thresholds.low) return 'low'
  if (balance >= thresholds.high) return 'high'
  return 'mid'
}

function loadThresholds(): BalanceThresholds {
  try {
    const raw = localStorage.getItem(THRESHOLDS_STORAGE_KEY)
    if (!raw) return DEFAULT_THRESHOLDS
    const parsed = JSON.parse(raw) as Partial<BalanceThresholds>
    return {
      low: Number.isFinite(parsed.low) ? Number(parsed.low) : DEFAULT_THRESHOLDS.low,
      high: Number.isFinite(parsed.high) ? Number(parsed.high) : DEFAULT_THRESHOLDS.high,
    }
  } catch {
    return DEFAULT_THRESHOLDS
  }
}

const pad = (n: number) => String(n).padStart(2, '0')

/** 表示単位の設定を織り込んだ、この画面で使うフォーマッタ */
function useLedgerFormat() {
  const { formatJPY, formatSignedJPY, formatPlain } = useAmountFormat()
  /** 明細行用: 符号付きの素の数値（¥ なし） */
  const formatSigned = (amount: number) =>
    `${signPrefix(amount)}${formatPlain(Math.abs(amount))}`
  return { formatJPY, formatSignedJPY, formatSigned }
}

function buildMonths(year: number, entries: LedgerEntry[], openingBalance: number): MonthBlock[] {
  const byDate = new Map<string, LedgerEntry[]>()
  for (const e of entries) {
    const list = byDate.get(e.date)
    if (list) list.push(e)
    else byDate.set(e.date, [e])
  }

  let balance = openingBalance
  const months: MonthBlock[] = []

  for (let month = 1; month <= 12; month++) {
    const lastDay = new Date(year, month, 0).getDate()
    const days: DayRow[] = []
    let income = 0
    let expense = 0

    for (let day = 1; day <= lastDay; day++) {
      const dayEntries = byDate.get(`${year}-${pad(month)}-${pad(day)}`) ?? []
      let total = 0
      for (const e of dayEntries) {
        // 残高（total）には集計対象外も効かせる。効かせないと年初残高の逆算とずれて
        // 年末残高が現在残高に着地しなくなる。月の収支合計からだけ外す
        if (e.type === 'income') {
          total += e.amount
          if (!e.excluded) income += e.amount
        } else {
          total -= e.amount
          if (!e.excluded) expense += e.amount
        }
      }
      balance += total
      days.push({ day, weekday: new Date(year, month - 1, day).getDay(), entries: dayEntries, total, balance })
    }

    months.push({ month, days, income, expense, closing: balance })
  }

  return months
}

export default function AnnualLedger() {
  const { accessToken } = useAuth()
  const { t } = useLanguage()
  const { formatJPY, formatSigned } = useLedgerFormat()
  const today = new Date()
  const [viewMode, setViewMode] = useState<ViewMode>('annual')
  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth() + 1)
  const [entries, setEntries] = useState<LedgerEntry[]>([])
  const [openingBalance, setOpeningBalance] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hideEmpty, setHideEmpty] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const searchQuery = searchInput.trim().toLowerCase()
  const [thresholds, setThresholds] = useState<BalanceThresholds>(loadThresholds)
  const [thresholdsOpen, setThresholdsOpen] = useState(false)

  const [accounts, setAccounts] = useState<Account[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [filterAccountId, setFilterAccountId] = useState('')
  const [filterType, setFilterType] = useState<TransactionType | 'all'>('all')
  const [filterCategoryId, setFilterCategoryId] = useState('')

  useEffect(() => {
    if (!accessToken) return
    accountsApi.list(accessToken, { includeArchived: false })
      // 証券口座はこの表の集計対象外なので、絞り込みの選択肢からも外す
      .then((res) => setAccounts(res.data.filter((a) => a.type !== 'investment')))
      .catch(() => undefined)
    categoriesApi.list(accessToken)
      .then((res) => setCategories(res.data))
      .catch(() => undefined)
  }, [accessToken])

  const hasActiveFilter = searchQuery !== '' || filterAccountId !== '' || filterType !== 'all' || filterCategoryId !== ''

  const matchesFilter = useCallback(
    (e: LedgerEntry) => {
      if (searchQuery && !e.description.toLowerCase().includes(searchQuery)) return false
      if (filterAccountId && e.accountId !== filterAccountId) return false
      if (filterType !== 'all' && e.type !== filterType) return false
      if (filterCategoryId && e.categoryId !== filterCategoryId) return false
      return true
    },
    [searchQuery, filterAccountId, filterType, filterCategoryId],
  )

  useEffect(() => {
    localStorage.setItem(THRESHOLDS_STORAGE_KEY, JSON.stringify(thresholds))
  }, [thresholds])

  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!accessToken) return
    setLoading(true)
    setError(null)
    reportsApi.annualLedger(accessToken, year)
      .then((res) => {
        setEntries(res.data.entries)
        setOpeningBalance(res.data.openingBalance)
      })
      .catch(() => {
        setEntries([])
        setOpeningBalance(0)
        setError(t('ledger_fetch_error'))
      })
      .finally(() => setLoading(false))
  }, [accessToken, year, t])

  const months = useMemo(
    () => buildMonths(year, entries, openingBalance),
    [year, entries, openingBalance],
  )

  /**
   * 表示する日付行（1〜31）。
   * 検索中はどの月にも一致する取引がない日を落とし、検索していないときは
   * hideEmpty のときにどの月にも取引がない日を落とす
   */
  const dayNumbers = useMemo(() => {
    const all = Array.from({ length: 31 }, (_, i) => i + 1)
    if (hasActiveFilter) {
      return all.filter((day) =>
        months.some((m) => m.days[day - 1]?.entries.some((e) => matchesFilter(e))),
      )
    }
    if (!hideEmpty) return all
    return all.filter((day) => months.some((m) => (m.days[day - 1]?.entries.length ?? 0) > 0))
  }, [months, hideEmpty, hasActiveFilter, matchesFilter])

  const yearIncome = months.reduce((sum, m) => sum + m.income, 0)
  const yearExpense = months.reduce((sum, m) => sum + m.expense, 0)
  const closingBalance = months[11]?.closing ?? openingBalance

  const isAnnual = viewMode === 'annual'
  const isCurrentYear = year === today.getFullYear()
  const todayMonth = today.getMonth() + 1
  const todayDay = today.getDate()
  const isAtCurrent = isAnnual ? isCurrentYear : isCurrentYear && month === todayMonth

  const monthBlock = months[month - 1]
  // 月初残高は前月末の残高。1月だけは年初残高そのもの
  const monthOpening = month === 1 ? openingBalance : months[month - 2]?.closing ?? openingBalance

  // 印と注記は、いま見えている期間に集計対象外の取引があるときだけ出す
  const hasExcluded = useMemo(
    () =>
      isAnnual
        ? entries.some((e) => e.excluded)
        : monthBlock?.days.some((d) => d.entries.some((e) => e.excluded)) ?? false,
    [isAnnual, entries, monthBlock],
  )

  /** 月間表示の週配置（日曜始まり。月初前と月末後の余白は null） */
  const weeks = useMemo(() => {
    if (isAnnual || !monthBlock) return []
    const cells: (DayRow | null)[] = Array.from(
      { length: new Date(year, month - 1, 1).getDay() },
      () => null,
    )
    cells.push(...monthBlock.days)
    while (cells.length % 7 !== 0) cells.push(null)
    return Array.from({ length: cells.length / 7 }, (_, i) => cells.slice(i * 7, i * 7 + 7))
  }, [isAnnual, monthBlock, year, month])

  const prevPeriod = () => {
    if (isAnnual) { setYear((y) => y - 1); return }
    if (month === 1) { setYear((y) => y - 1); setMonth(12) }
    else setMonth((m) => m - 1)
  }
  const nextPeriod = () => {
    if (isAnnual) { setYear((y) => y + 1); return }
    if (month === 12) { setYear((y) => y + 1); setMonth(1) }
    else setMonth((m) => m + 1)
  }
  const goToCurrent = () => { setYear(today.getFullYear()); setMonth(todayMonth) }

  const scrollToMonth = useCallback((month: number, smooth: boolean) => {
    const container = scrollRef.current
    if (!container) return
    container.scrollTo({ left: (month - 1) * MONTH_WIDTH, behavior: smooth ? 'smooth' : 'auto' })
  }, [])

  // 今年を開いたときは今月が視界に入る位置から始める
  useEffect(() => {
    if (loading || !isAnnual || !isCurrentYear) return
    scrollToMonth(todayMonth, false)
  }, [loading, isAnnual, isCurrentYear, todayMonth, scrollToMonth])

  const viewTab = (mode: ViewMode, label: string) => (
    <button
      onClick={() => setViewMode(mode)}
      data-testid={`ledger-view-${mode}`}
      className={segItem(viewMode === mode)}
    >
      {label}
    </button>
  )

  return (
    <div className="h-full flex flex-col min-h-0 gap-4" data-testid="annual-ledger-page">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div>
          <h1 className={pageTitle}>{t('ledger_title')}</h1>
          <p className={pageSubtitle}>{t('ledger_subtitle')}</p>
        </div>

        <div className={segGroup}>
          {viewTab('monthly', t('reports_view_monthly'))}
          {viewTab('annual', t('reports_view_annual'))}
        </div>

        <button onClick={prevPeriod} data-testid="ledger-prev-period" className={btnNav}>
          ◀
        </button>
        <span className="text-base font-semibold text-gray-900 min-w-[6rem] text-center tabular-nums dark:text-gray-100">
          {isAnnual
            ? year
            : t('common_year_month').replace('{year}', String(year)).replace('{month}', String(month))}
        </span>
        <button onClick={nextPeriod} data-testid="ledger-next-period" className={btnNav}>
          ▶
        </button>
        {!isAtCurrent ? (
          <button onClick={goToCurrent} className={btnAccentSm}>
            {t('common_today')}
          </button>
        ) : (
          // 年間表示は今年でも今月が画面外にいることがあるので、そこへ寄せる導線を残す
          isAnnual && (
            <button onClick={() => scrollToMonth(todayMonth, true)} className={btnAccentSm}>
              {t('ledger_jump_today')}
            </button>
          )
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pl-3 border-l border-gray-200 text-xs dark:border-gray-700">
          <span className="text-gray-500 dark:text-gray-400">
            {t(isAnnual ? 'ledger_opening_balance' : 'ledger_month_opening_balance')}{' '}
            <span className="font-semibold text-gray-900 tabular-nums dark:text-gray-100">
              {formatJPY(isAnnual ? openingBalance : monthOpening)}
            </span>
          </span>
          <span className="text-gray-500 dark:text-gray-400">
            {t(isAnnual ? 'ledger_year_income' : 'ledger_month_income')}{' '}
            <span className="font-semibold text-green-600 tabular-nums dark:text-green-400">
              {formatJPY(isAnnual ? yearIncome : monthBlock?.income ?? 0)}
            </span>
          </span>
          <span className="text-gray-500 dark:text-gray-400">
            {t(isAnnual ? 'ledger_year_expense' : 'ledger_month_expense')}{' '}
            <span className="font-semibold text-red-600 tabular-nums dark:text-red-400">
              {formatJPY(isAnnual ? yearExpense : monthBlock?.expense ?? 0)}
            </span>
          </span>
          <span className="text-gray-500 dark:text-gray-400">
            {t(isAnnual ? 'ledger_closing_balance' : 'ledger_month_closing_balance')}{' '}
            <span className="font-semibold text-indigo-600 tabular-nums dark:text-indigo-400">
              {formatJPY(isAnnual ? closingBalance : monthBlock?.closing ?? monthOpening)}
            </span>
          </span>
          {hasExcluded && (
            <span className="text-gray-400 dark:text-gray-500">※ {t('ledger_excluded_note')}</span>
          )}
        </div>

        <select
          value={filterAccountId}
          onChange={(e) => setFilterAccountId(e.target.value)}
          data-testid="ledger-filter-account"
          className="ml-auto text-xs border border-gray-200 rounded-lg px-3 py-1.5 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="">{t('transactions_filter_all_accounts')}</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as TransactionType | 'all')}
          data-testid="ledger-filter-type"
          className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="all">{t('transactions_filter_all_types')}</option>
          <option value="income">{t('type_income')}</option>
          <option value="expense">{t('type_expense')}</option>
          <option value="transfer">{t('type_transfer')}</option>
        </select>
        <CategoryCombobox
          categories={categories}
          value={filterCategoryId}
          onChange={setFilterCategoryId}
          emptyLabel={t('transactions_filter_all_categories')}
          data-testid="ledger-filter-category"
          className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
        />
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t('ledger_search_placeholder')}
          data-testid="ledger-search-input"
          className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 min-w-[160px] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
        />

        {isAnnual && (
          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer whitespace-nowrap dark:text-gray-300">
            <input
              type="checkbox"
              checked={hideEmpty}
              onChange={(e) => setHideEmpty(e.target.checked)}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800"
            />
            {t('ledger_hide_empty')}
          </label>
        )}

        <button
          onClick={() => setThresholdsOpen((v) => !v)}
          data-testid="ledger-threshold-toggle"
          aria-expanded={thresholdsOpen}
          className={btnSecondarySm}
        >
          <span className="flex items-center gap-0.5">
            <span className="w-2 h-2 rounded-full bg-red-400" />
            <span className="w-2 h-2 rounded-full bg-amber-400" />
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
          </span>
          {t('ledger_threshold_title')}
        </button>
      </div>

      {thresholdsOpen && (
        <div
          data-testid="ledger-threshold-panel"
          className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs dark:border-gray-800 dark:bg-gray-900"
        >
          <label className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <span className="w-2.5 h-2.5 rounded-sm bg-red-200 dark:bg-red-500/40" />
              {t('ledger_threshold_low_label')}
            </span>
            <input
              type="number"
              step={100000}
              value={thresholds.low}
              data-testid="ledger-threshold-low"
              onChange={(e) => setThresholds((prev) => ({ ...prev, low: Number(e.target.value) }))}
              className="w-32 px-2 py-1 text-xs tabular-nums border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            />
          </label>
          <label className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <span className="w-2.5 h-2.5 rounded-sm bg-emerald-200 dark:bg-emerald-500/40" />
              {t('ledger_threshold_high_label')}
            </span>
            <input
              type="number"
              step={100000}
              value={thresholds.high}
              data-testid="ledger-threshold-high"
              onChange={(e) => setThresholds((prev) => ({ ...prev, high: Number(e.target.value) }))}
              className="w-32 px-2 py-1 text-xs tabular-nums border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            />
          </label>
          <button
            onClick={() => setThresholds(DEFAULT_THRESHOLDS)}
            className={`${btnAccentSm} whitespace-nowrap`}
          >
            {t('ledger_threshold_reset')}
          </button>
          <span className="ml-auto flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
            <span className={`px-1.5 py-0.5 rounded tabular-nums ${TONE_CLASSES.low}`}>
              {`< ${formatJPY(thresholds.low)}`}
            </span>
            <span className={`px-1.5 py-0.5 rounded tabular-nums ${TONE_CLASSES.mid}`}>
              {`${formatJPY(thresholds.low)} – ${formatJPY(thresholds.high)}`}
            </span>
            <span className={`px-1.5 py-0.5 rounded tabular-nums ${TONE_CLASSES.high}`}>
              {`≥ ${formatJPY(thresholds.high)}`}
            </span>
          </span>
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700 dark:bg-red-950 dark:border-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">
          {t('common_loading')}
        </div>
      ) : !isAnnual ? (
        <div
          data-testid="ledger-month-grid"
          className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900"
        >
          <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50 text-[11px] font-medium dark:border-gray-700 dark:bg-gray-800">
            {DAY_KEYS.map((key, i) => (
              <div
                key={key}
                className={`px-2 py-1.5 text-center ${
                  i === 0
                    ? 'text-red-500'
                    : i === 6
                    ? 'text-blue-500'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {t(key)}
              </div>
            ))}
          </div>
          <div
            className="flex-1 min-h-0 grid grid-cols-7 overflow-auto"
            style={{ gridTemplateRows: `repeat(${weeks.length || 1}, minmax(88px, 1fr))` }}
          >
            {weeks.flat().map((row, i) => (
              <MonthDayCell
                key={row ? row.day : `blank-${i}`}
                row={row}
                isToday={isCurrentYear && month === todayMonth && row?.day === todayDay}
                thresholds={thresholds}
                hasActiveFilter={hasActiveFilter}
                matchesFilter={matchesFilter}
              />
            ))}
          </div>
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="flex-1 min-h-0 overflow-auto rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900"
        >
          <table
            className="table-fixed border-separate border-spacing-0 text-[11px]"
            style={{ width: MONTH_WIDTH * months.length }}
          >
            <colgroup>
              {months.map((m) =>
                COL_WIDTHS.map((w, i) => <col key={`${m.month}-${i}`} style={{ width: w }} />),
              )}
            </colgroup>
            <thead>
              <tr>
                {months.map((m) => (
                  <th
                    key={m.month}
                    colSpan={COL_WIDTHS.length}
                    data-testid={`ledger-month-${m.month}`}
                    style={{ height: MONTH_HEADER_HEIGHT }}
                    className="sticky top-0 z-20 bg-white px-2 border-b border-l-2 border-gray-200 first:border-l-0 dark:bg-gray-900 dark:border-gray-700"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {t('common_month_n').replace('{n}', String(m.month))}
                      </span>
                      <span className="text-[11px] font-normal tabular-nums text-gray-500 dark:text-gray-400">
                        <span className="text-green-600 dark:text-green-400">{formatSigned(m.income)}</span>
                        {' / '}
                        <span className="text-red-600 dark:text-red-400">{formatSigned(-m.expense)}</span>
                        {' → '}
                        <span className={`font-semibold px-1 rounded ${TONE_CLASSES[balanceTone(m.closing, thresholds)]}`}>
                          {formatJPY(m.closing)}
                        </span>
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
              <tr className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
                {months.map((m) => (
                  <HeaderCells key={m.month} isFirstMonth={m.month === 1} />
                ))}
              </tr>
            </thead>
            <tbody>
              {dayNumbers.length === 0 ? (
                <tr>
                  <td
                    colSpan={COL_WIDTHS.length * months.length}
                    className="px-3 py-6 text-center text-xs text-gray-400 dark:text-gray-500"
                  >
                    {hasActiveFilter ? t('ledger_search_no_match') : t('ledger_month_empty')}
                  </td>
                </tr>
              ) : (
                dayNumbers.map((day) => (
                  <tr key={day} className="align-top">
                    {months.map((m) => (
                      <DayCells
                        key={m.month}
                        row={m.days[day - 1]}
                        isFirstMonth={m.month === 1}
                        isToday={isCurrentYear && m.month === todayMonth && day === todayDay}
                        thresholds={thresholds}
                        hasActiveFilter={hasActiveFilter}
                        matchesFilter={matchesFilter}
                      />
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/** 月間表示の1日ぶんのマス。説明・金額を積み、足元にその日の合計と残高を出す */
function MonthDayCell({
  row,
  isToday,
  thresholds,
  hasActiveFilter,
  matchesFilter,
}: {
  row: DayRow | null
  isToday: boolean
  thresholds: BalanceThresholds
  hasActiveFilter: boolean
  matchesFilter: (e: LedgerEntry) => boolean
}) {
  const { formatJPY, formatSigned } = useLedgerFormat()
  const cell =
    'border-r border-b border-gray-100 dark:border-gray-800 [&:nth-child(7n)]:border-r-0'

  // 月初前・月末後の余白は、日付のないマスとして敷いておく
  if (!row) return <div className={`${cell} bg-gray-50/60 dark:bg-gray-800/30`} />

  const visibleEntries = hasActiveFilter ? row.entries.filter((e) => matchesFilter(e)) : row.entries
  const dayTone = isToday
    ? 'font-bold text-indigo-600 dark:text-indigo-400'
    : row.weekday === 0
    ? 'text-red-500'
    : row.weekday === 6
    ? 'text-blue-500'
    : 'text-gray-500 dark:text-gray-400'
  const bg = isToday
    ? 'bg-indigo-50 dark:bg-indigo-500/15'
    : row.weekday === 0
    ? 'bg-red-50/50 dark:bg-red-500/5'
    : row.weekday === 6
    ? 'bg-blue-50/50 dark:bg-blue-500/5'
    : ''

  return (
    <div className={`${cell} ${bg} flex flex-col min-h-0 px-1.5 py-1`}>
      <div className={`text-[11px] leading-4 tabular-nums ${dayTone}`}>{row.day}</div>
      <div className="flex-1 min-h-0 overflow-y-auto py-0.5">
        {visibleEntries.map((e) => (
          <div
            key={e.id}
            className="flex items-baseline gap-1 text-[11px] leading-4"
            title={`${e.description}${e.categoryName ? ` / ${e.categoryName}` : ''} (${e.accountName})`}
          >
            <span
              className={`flex-1 truncate ${
                e.excluded ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-200'
              }`}
            >
              {e.excluded && <span aria-hidden>※ </span>}
              {e.description}
            </span>
            <span
              className={`tabular-nums whitespace-nowrap ${
                e.excluded
                  ? 'text-gray-400 dark:text-gray-500'
                  : e.type === 'income'
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {formatSigned(e.type === 'income' ? e.amount : -e.amount)}
            </span>
          </div>
        ))}
      </div>
      <div className="flex items-baseline justify-between gap-1 text-[10px] leading-4">
        <span
          className={`tabular-nums ${
            row.total >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
          }`}
        >
          {row.entries.length > 0 ? formatSigned(row.total) : ''}
        </span>
        <span
          className={`px-1 rounded font-medium tabular-nums ${TONE_CLASSES[balanceTone(row.balance, thresholds)]}`}
        >
          {formatJPY(row.balance)}
        </span>
      </div>
    </div>
  )
}

function HeaderCells({ isFirstMonth }: { isFirstMonth: boolean }) {
  const { t } = useLanguage()
  const base = 'sticky bg-gray-50 px-1 py-1 border-b border-gray-200 font-medium dark:bg-gray-800 dark:border-gray-700'
  return (
    <>
      <th
        style={{ top: MONTH_HEADER_HEIGHT }}
        className={`${base} z-10 text-left ${isFirstMonth ? '' : 'border-l-2'}`}
      />
      <th style={{ top: MONTH_HEADER_HEIGHT }} className={`${base} z-10 text-left px-2`}>
        {t('ledger_col_desc')}
      </th>
      <th style={{ top: MONTH_HEADER_HEIGHT }} className={`${base} z-10 text-right`}>
        {t('ledger_col_amount')}
      </th>
      <th style={{ top: MONTH_HEADER_HEIGHT }} className={`${base} z-10 text-right`}>
        {t('ledger_col_total')}
      </th>
      <th style={{ top: MONTH_HEADER_HEIGHT }} className={`${base} z-10 text-right`}>
        {t('ledger_col_balance')}
      </th>
    </>
  )
}

function DayCells({
  row,
  isFirstMonth,
  isToday,
  thresholds,
  hasActiveFilter,
  matchesFilter,
}: {
  row: DayRow | undefined
  isFirstMonth: boolean
  isToday: boolean
  thresholds: BalanceThresholds
  hasActiveFilter: boolean
  matchesFilter: (e: LedgerEntry) => boolean
}) {
  const { t } = useLanguage()
  const { formatJPY, formatSignedJPY, formatSigned } = useLedgerFormat()
  const cell = 'py-1 border-b border-gray-100 dark:border-gray-800'
  const monthEdge = isFirstMonth ? '' : 'border-l-2 border-l-gray-200 dark:border-l-gray-700'
  const visibleEntries = hasActiveFilter
    ? row?.entries.filter((e) => matchesFilter(e)) ?? []
    : row?.entries ?? []

  // その月に存在しない日（2/30 など）は空欄で埋めて行を揃える
  if (!row) {
    const empty = 'bg-gray-50/60 dark:bg-gray-800/30'
    return (
      <>
        <td className={`${cell} ${monthEdge} ${empty}`} />
        <td className={`${cell} ${empty}`} />
        <td className={`${cell} ${empty}`} />
        <td className={`${cell} ${empty}`} />
        <td className={`${cell} ${empty}`} />
      </>
    )
  }

  const weekdayTone =
    row.weekday === 0
      ? 'text-red-500 bg-red-50 dark:bg-red-500/10'
      : row.weekday === 6
      ? 'text-blue-500 bg-blue-50 dark:bg-blue-500/10'
      : 'text-gray-500 dark:text-gray-400'
  const todayTone = isToday ? 'bg-indigo-50 dark:bg-indigo-500/15' : ''

  return (
    <>
      <td className={`${cell} ${monthEdge} px-1 tabular-nums whitespace-nowrap ${isToday ? todayTone : weekdayTone}`}>
        <span className={isToday ? 'font-bold text-indigo-600 dark:text-indigo-400' : ''}>{row.day}</span>{' '}
        <span className="text-[10px]">{t(DAY_KEYS[row.weekday])}</span>
      </td>
      <td className={`${cell} px-2 text-gray-700 dark:text-gray-200 ${todayTone}`}>
        {visibleEntries.map((e) => (
          <div
            key={e.id}
            className={`truncate leading-4 ${e.excluded ? 'text-gray-400 dark:text-gray-500' : ''}`}
            title={`${e.description}${e.categoryName ? ` / ${e.categoryName}` : ''} (${e.accountName})`}
          >
            {e.excluded && <span aria-hidden>※ </span>}
            {e.description}
          </div>
        ))}
      </td>
      <td className={`${cell} px-1 text-right tabular-nums ${todayTone}`}>
        {visibleEntries.map((e) => (
          <div
            key={e.id}
            className={`leading-4 ${
              e.excluded
                ? 'text-gray-400 dark:text-gray-500'
                : e.type === 'income'
                ? 'text-green-600 dark:text-green-400'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            {formatSigned(e.type === 'income' ? e.amount : -e.amount)}
          </div>
        ))}
      </td>
      <td className={`${cell} px-1 text-right tabular-nums leading-4 ${todayTone}`}>
        {row.entries.length > 0 && (
          <span
            className={`font-medium ${
              row.total >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
            }`}
          >
            {formatSignedJPY(row.total)}
          </span>
        )}
      </td>
      <td
        className={`${cell} px-1 text-right tabular-nums leading-4 font-medium ${TONE_CLASSES[balanceTone(row.balance, thresholds)]}`}
      >
        {formatJPY(row.balance)}
      </td>
    </>
  )
}
